import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { RotateCcwIcon, SearchIcon } from 'lucide-react';
import { movementLabel } from './pharmacyUtils';
export function StockMovementHistory() {
  const {
    currentUser,
    stockMovements,
    patients,
    reverseDispense
  } = useData();
  const [query, setQuery] = useState('');
  const [message, setMessage] = useState('');
  const [reversing, setReversing] = useState<string | null>(null);
  const manager = currentUser?.role === 'Pharmacy Manager';
  const movements = useMemo(() => stockMovements.filter(movement => {
    const patient = movement.patientId ? patients.find(item => item.id === movement.patientId) : undefined;
    return `${movement.medicineName} ${movement.type} ${movement.actionPerformed || ''} ${movement.actorName} ${movement.authorisedUserName || ''} ${movement.batchNumber || ''} ${movement.notes || ''} ${movement.reason || ''} ${movement.prescriptionId || ''} ${patient?.firstName || ''} ${patient?.lastName || ''}`.toLowerCase().includes(query.trim().toLowerCase());
  }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()), [patients, query, stockMovements]);
  const reverse = async (movementId: string) => {
    const comment = window.prompt('Enter the mandatory manager comment for this dispensing reversal:');
    if (!comment) return;
    setReversing(movementId);
    const result = await reverseDispense(movementId, comment);
    setReversing(null);
    setMessage(result.ok ? 'Dispensing was reversed with a compensating inventory movement. The prescription is pending again.' : result.error || 'Unable to reverse dispensing.');
  };
  return <div className="space-y-5" data-id="element-5447" data-mp="7qeef7sj22hl">
      <div data-id="element-5448" data-mp="2s4n589xbm19">
        <h2 className="text-xl font-bold text-slate-950" data-id="element-5449" data-mp="2rl1phl2efih">
          Stock Movement History
        </h2>
        <p className="mt-1 text-sm text-slate-500" data-id="element-5450" data-mp="3p9d7i053qlbg">
          Permanent, live audit trail for every inventory transaction. Ledger
          records cannot be deleted from this workspace.
        </p>
      </div>
      {message && <p role="status" className="rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-700" data-id="element-5451" data-mp="3fd9xy97x7keh">
          {message}
        </p>}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-5452" data-mp="bugp29q37jqn">
        <div className="border-b border-slate-100 p-4" data-id="element-5453" data-mp="gul3lu7fq4e8">
          <div className="relative max-w-xl" data-id="element-5454" data-mp="gw5zbn5hjtdo">
            <SearchIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-5455" data-mp="25yre1d3tsr8" />
            <input aria-label="Search stock movements" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search medicine, batch, action, user, authoriser, patient or reference" className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600" data-id="element-5456" data-mp="73i4gxy2fzhl" />
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-5457" data-mp="2t3a63yjppyyz">
          <table className="min-w-[1550px] w-full divide-y divide-slate-100" data-id="element-5458" data-mp="2t206h4x7oiq8">
            <thead className="bg-slate-50" data-id="element-5459" data-mp="2p1ga43kgpj55">
              <tr data-id="element-5460" data-mp="2p2hw3330nq1n">
                {['Date & time', 'Medicine / batch allocations', 'Action / reason', 'Previous → new', 'Changed', 'Responsible / authorised', 'Patient / reference', 'Comment', ''].map(heading => <th key={heading} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-5461" data-mp="25cebktnvgpo1">
                    {heading}
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-5462" data-mp="fzhlmj3yfbex">
              {movements.map(movement => {
              const patient = movement.patientId ? patients.find(item => item.id === movement.patientId) : undefined;
              const canReverse = manager && movement.type === 'Dispense' && !stockMovements.some(item => item.reversalOfMovementId === movement.id);
              return <tr key={movement.id} className="align-top" data-id="element-5463" data-mp="18mlxn81gsq07">
                    <td className="whitespace-nowrap px-5 py-3.5 text-xs text-slate-600" data-id="element-5464" data-mp="18n3xsfr8zode">
                      {new Date(movement.createdAt).toLocaleString()}
                    </td>
                    <td className="px-5 py-3.5" data-id="element-5465" data-mp="1im325m4zcxul">
                      <p className="text-sm font-semibold text-slate-900" data-id="element-5466" data-mp="1mltw1ypszu3c">
                        {movement.medicineName}
                      </p>
                      <p className="mt-1 text-xs text-slate-500" data-id="element-5467" data-mp="375paxk9cn94h">
                        {movement.allocations?.length ? movement.allocations.map(allocation => `${allocation.batchNumber} (${allocation.quantityBefore}→${allocation.quantityAfter})`).join(', ') : movement.batchNumber || 'No batch'}
                      </p>
                    </td>
                    <td className="px-5 py-3.5" data-id="element-5468" data-mp="3h1jymtsfjue4">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700" data-id="element-5469" data-mp="3h08uutyzimn0">
                        {movement.actionPerformed || movementLabel(movement)}
                      </span>
                      <p className="mt-1 text-xs text-slate-500" data-id="element-5470" data-mp="8migfxj2yur1">
                        Reason: {movement.reason || '—'}
                      </p>
                    </td>
                    <td className="px-5 py-3.5 text-sm font-medium text-slate-700" data-id="element-5471" data-mp="22v5nnsblmw3m">
                      {movement.quantityBefore} → {movement.quantityAfter}
                    </td>
                    <td className={`px-5 py-3.5 text-sm font-bold ${movement.quantityDelta > 0 ? 'text-emerald-700' : 'text-red-700'}`} data-id="element-5472" data-mp="1t41b0z9ebnby">
                      {movement.quantityDelta > 0 ? '+' : ''}
                      {movement.quantityDelta}
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-700" data-id="element-5473" data-mp="1o6rburw7fxax">
                      <p data-id="element-5474" data-mp="1a704ha071f5w">{movement.actorName}</p>
                      <p className="mt-1 text-xs text-slate-500" data-id="element-5475" data-mp="1f4rws54r33am">
                        Authorised: {movement.authorisedUserName || '—'}
                      </p>
                    </td>
                    <td className="px-5 py-3.5 text-sm text-slate-700" data-id="element-5476" data-mp="34hvduej7cy9q">
                      {patient ? <p data-id="element-5477" data-mp="3jmr53txelcsv">
                          {patient.firstName} {patient.lastName}
                        </p> : <p data-id="element-5478" data-mp="3eoxxcwggcaqh">—</p>}
                      <p className="mt-1 text-xs text-slate-500" data-id="element-5479" data-mp="3set19g0fw6xc">
                        {movement.prescriptionId ? `Rx: ${movement.prescriptionId}` : movement.referenceId ? `Ref: ${movement.referenceId}` : '—'}
                      </p>
                    </td>
                    <td className="max-w-xs px-5 py-3.5 text-sm text-slate-600" data-id="element-5480" data-mp="1aumuac4u4pe">
                      {movement.notes || '—'}
                    </td>
                    <td className="px-5 py-3.5 text-right" data-id="element-5481" data-mp="b6o8qkbqraju">
                      {canReverse && <button type="button" disabled={reversing === movement.id} onClick={() => reverse(movement.id)} className="inline-flex items-center whitespace-nowrap rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-50" data-id="element-5482" data-mp="2gz4h0weklm08">
                          <RotateCcwIcon className="mr-1.5 h-3.5 w-3.5" data-id="element-5483" data-mp="3bb58adnvprqs" />
                          {reversing === movement.id ? 'Reversing…' : 'Reverse dispense'}
                        </button>}
                      {movement.reversalOfMovementId && <span className="text-xs font-semibold text-emerald-700" data-id="element-5484" data-mp="2rlbdrrts4yiv">
                          Reversal
                        </span>}
                    </td>
                  </tr>;
            })}
              {!movements.length && <tr data-id="element-5485" data-mp="btx9xsv3xt9z">
                  <td colSpan={9} className="px-5 py-14 text-center text-sm text-slate-500" data-id="element-5486" data-mp="bt4918itxxzr">
                    No stock movements match your search.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>;
}